import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface ActivityDrawerProps {
    open: boolean;
    onClose: () => void;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    width?: 'md' | 'lg' | 'xl' | '2xl';
    children: React.ReactNode;
    footer?: React.ReactNode;
}

const widthClasses: Record<string, string> = {
    md: 'max-w-md',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl',
};

/**
 * Right-side slide-over drawer used across the Field Activities workspace.
 */
const ActivityDrawer: React.FC<ActivityDrawerProps> = ({
    open,
    onClose,
    title,
    subtitle,
    width = 'lg',
    children,
    footer,
}) => {
    return (
        <Transition show={open} as={Fragment}>
            <Dialog as="div" className="relative z-[2000]" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-hidden">
                    <div className="absolute inset-y-0 right-0 flex w-full max-w-full">
                        <Transition.Child
                            as={Fragment}
                            enter="transform transition ease-out duration-300"
                            enterFrom="translate-x-full"
                            enterTo="translate-x-0"
                            leave="transform transition ease-in duration-200"
                            leaveFrom="translate-x-0"
                            leaveTo="translate-x-full"
                        >
                            <Dialog.Panel className={`flex h-full w-full ${widthClasses[width]} flex-col bg-white shadow-2xl`}>
                                <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
                                    <div className="min-w-0 pr-4">
                                        <Dialog.Title as="h2" className="text-lg font-black text-slate-900">
                                            {title}
                                        </Dialog.Title>
                                        {subtitle && <div className="mt-1 text-sm text-slate-500">{subtitle}</div>}
                                    </div>
                                    <button
                                        onClick={onClose}
                                        className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                                        aria-label="Close panel"
                                    >
                                        <XMarkIcon className="h-6 w-6" />
                                    </button>
                                </div>

                                <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

                                {footer && (
                                    <div className="border-t border-slate-200 bg-slate-50 px-6 py-4">{footer}</div>
                                )}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default ActivityDrawer;
